import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { IExtraChecklistItem } from "@rentiohq/shared-frontend/dist/reduxV2/preferences/preference.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Button, Card, Switch } from "@rentiohq/web-shared/dist/components";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { ChecklistItem } from "../ChecklistItem";
import * as S from "./ChecklistSection.styles";

export interface IChecklistSectionRef {
  validateAll: () => boolean;
}

interface IProps {
  title: string;
  description: string;
  enabled: boolean;
  items: IExtraChecklistItem[];
  onToggleEnabled: (value: boolean) => void;
  onItemsChange: (items: IExtraChecklistItem[]) => void;
}

export const ChecklistSection = forwardRef<IChecklistSectionRef, IProps>(
  (
    { title, description, enabled, items, onToggleEnabled, onItemsChange },
    ref,
  ) => {
    const [errors, setErrors] = useState<
      Record<number, { title?: string; description?: string }>
    >({});

    useImperativeHandle(ref, () => ({
      validateAll: () => {
        const newErrors: Record<
          number,
          { title?: string; description?: string }
        > = {};
        let isValid = true;
        items.forEach(item => {
          const itemErrors: { title?: string; description?: string } = {};
          if (!item.title.trim()) {
            itemErrors.title = getLocalizedText(
              "settings.checklist.item.title.required",
            );
            isValid = false;
          } else if (item.title.length > 30) {
            itemErrors.title = getLocalizedText(
              "settings.checklist.item.title.max_length",
            );
            isValid = false;
          }
          if (!item.description.trim()) {
            itemErrors.description = getLocalizedText(
              "settings.checklist.item.description.required",
            );
            isValid = false;
          } else if (item.description.length > 60) {
            itemErrors.description = getLocalizedText(
              "settings.checklist.item.description.max_length",
            );
            isValid = false;
          }
          if (Object.keys(itemErrors).length > 0) {
            newErrors[item.index] = itemErrors;
          }
        });
        setErrors(newErrors);
        return isValid;
      },
    }));

    const dragItem = useRef<number | null>(null);
    const dragOverItem = useRef<number | null>(null);

    const validateItem = (
      item: IExtraChecklistItem,
    ): { title?: string; description?: string } => {
      const itemErrors: { title?: string; description?: string } = {};
      if (!item.title.trim()) {
        itemErrors.title = getLocalizedText(
          "settings.checklist.item.title.required",
        );
      } else if (item.title.length > 30) {
        itemErrors.title = getLocalizedText(
          "settings.checklist.item.title.max_length",
        );
      }
      if (!item.description.trim()) {
        itemErrors.description = getLocalizedText(
          "settings.checklist.item.description.required",
        );
      } else if (item.description.length > 60) {
        itemErrors.description = getLocalizedText(
          "settings.checklist.item.description.max_length",
        );
      }
      return itemErrors;
    };

    const handleAddItem = () => {
      const newItem: IExtraChecklistItem = {
        title: "",
        description: "",
        completed: false,
        index: items.length,
      };
      onItemsChange([...items, newItem]);
    };

    const handleRemoveItem = (index: number) => {
      const updated = items
        .filter(item => item.index !== index)
        .map((item, i) => ({ ...item, index: i }));
      onItemsChange(updated);
      setErrors(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    };

    const handleItemChange = (
      index: number,
      field: "title" | "description",
      value: string,
    ) => {
      const updatedItems = items.map(item =>
        item.index === index ? { ...item, [field]: value } : item,
      );
      onItemsChange(updatedItems);

      const updatedItem = updatedItems.find(i => i.index === index);
      if (updatedItem) {
        const itemErrors = validateItem(updatedItem);
        setErrors(prev => ({ ...prev, [index]: itemErrors }));
      }
    };

    const handleDragStart = (index: number) => {
      dragItem.current = index;
    };

    const handleDragEnter = (index: number) => {
      dragOverItem.current = index;
    };

    const handleDragEnd = () => {
      if (
        dragItem.current === null ||
        dragOverItem.current === null ||
        dragItem.current === dragOverItem.current
      ) {
        dragItem.current = null;
        dragOverItem.current = null;
        return;
      }

      const reordered = [...items];
      const draggedItem = reordered.splice(dragItem.current, 1)[0];
      reordered.splice(dragOverItem.current, 0, draggedItem);
      onItemsChange(reordered.map((item, i) => ({ ...item, index: i })));

      dragItem.current = null;
      dragOverItem.current = null;
    };

    return (
      <Card>
        <S.CardHeader>
          <S.CardHeaderText>
            <S.CardTitle>{title}</S.CardTitle>
            <S.CardDescription>{description}</S.CardDescription>
          </S.CardHeaderText>
          <S.SwitchWrapper>
            <S.SwitchLabel>
              {getLocalizedText("settings.checklist.enable_extra_actions")}
            </S.SwitchLabel>
            <Switch isChecked={enabled} onChange={onToggleEnabled} />
          </S.SwitchWrapper>
        </S.CardHeader>
        <S.ItemsContainer isDisabled={!enabled}>
          {items.map(item => (
            <ChecklistItem
              key={item.index}
              item={item}
              enabled={enabled}
              errors={errors[item.index] ?? {}}
              onDragStart={() => handleDragStart(item.index)}
              onDragEnter={() => handleDragEnter(item.index)}
              onDragEnd={handleDragEnd}
              onItemChange={(field, value) =>
                handleItemChange(item.index, field, value)
              }
              onRemove={() => handleRemoveItem(item.index)}
            />
          ))}

          <Spacer weight={ESpacerWeight.W16} />

          <Button
            appearance="outline"
            isDisabled={!enabled}
            onClick={handleAddItem}
          >
            {getLocalizedText("settings.checklist.add_action")}
          </Button>
        </S.ItemsContainer>
      </Card>
    );
  },
);
